import { MutableState } from 'src/replicant/State';
import { AnalyticsEvent } from 'src/replicant/analytics';
import { PlayerID, clearFriendshipRequestsAfter } from './friends.ruleset';
import {
  getBuruFriendsCount,
  getFriendshipRequestMethod,
} from './friends.getters';

/**
 * Add new friend to friends list
 */
export function addFriend(
  state: MutableState,
  id: PlayerID,
  now: number,
): void {
  // Already in friends list
  if (state.buruFriends[id]) {
    // Remove unfriended status, if previously marked
    if (state.buruFriends[id].unfriendedAt) {
      delete state.buruFriends[id].unfriendedAt;
    }

    // Remove from user friends blacklist, if exists
    const index = state.friendsBlacklist.indexOf(id);
    if (index !== -1) {
      state.friendsBlacklist.splice(index, 1);
    }

    return;
  }

  // Add friend to friends list
  state.buruFriends[id] = {
    addedAt: now,
  };
}

/**
 * Remove friend from friends list
 */
export function removeFriend(state: MutableState, id: PlayerID): void {
  delete state.buruFriends[id];

  // Add to list of friends who were unfriended by the user
  if (!state.friendsBlacklist.includes(id)) {
    state.friendsBlacklist.push(id);
  }

  // Clear any pending invitations related to this friend
  clearFriendInvitations(state, id);
}

/**
 * Mark friend as unfriended, but still keep in the state
 */
export function markUnfriended(
  state: MutableState,
  id: PlayerID,
  now: number,
): void {
  // Skip if not in friends list
  if (!state.buruFriends[id]) return;

  // Mark friend as unfriended
  state.buruFriends[id].unfriendedAt = now;

  // Clear any pending invitations related to this friend
  clearFriendInvitations(state, id);
}

function clearFriendInvitations(state: MutableState, id: PlayerID) {
  // Remove gift invitations
  delete state.gifts.sent[id];
  delete state.gifts.received[id];

  // TODO: Should we consider other friend-specific invitations here? eg. duo, etc.
}

/**
 * Cleanup too old friendship requests from  state.friendshipRequests using clearFriendshipRequestsAfter rule
 */
export function cleanupFriendshipRequests(
  state: MutableState,
  now: number,
): void {
  const requests = state.friendshipRequests;

  Object.keys(requests).forEach((id) => {
    const request = requests[id];

    if (now - request.createdAt > clearFriendshipRequestsAfter) {
      delete requests[id];
    }
  });
}

/**
 * Drop accepted requests that have nothing left to do: the friendship lives in `buruFriends`,
 * and the only later reader is `trackAcceptedFriendshipRequests`, which still needs the sender's
 * copy until it has emitted `FriendAdd` for it.
 */
export function cleanupAcceptedFriendshipRequests(state: MutableState): void {
  const requests = state.friendshipRequests;

  for (const [id, request] of Object.entries(requests)) {
    if (request.status !== 'accepted') continue;

    const awaitingFriendAdd =
      request.owner.id === state.id && request.senderAcknowledgedAt === 0;
    if (!awaitingFriendAdd) {
      delete requests[id];
    }
  }
}

/**
 * Track pending friend requests that haven't been seen yet (receivedAt === 0).
 * Sets receivedAt to now and returns analytics events to send.
 */
export function trackReceivedFriendshipRequests(
  state: MutableState,
  now: number,
): AnalyticsEvent[] {
  const events: AnalyticsEvent[] = [];
  const friendCount = getBuruFriendsCount(state);

  for (const [id, request] of Object.entries(state.friendshipRequests)) {
    if (
      request.status === 'pending' &&
      request.owner.id !== state.id &&
      request.receivedAt === 0
    ) {
      events.push({
        eventType: 'FriendRequestReceive',
        eventProperties: {
          senderID: request.owner.id,
          friendCount,
          originFeature: request.originFeature,
          originSubFeature: request.originSubFeature,
          method: getFriendshipRequestMethod(request.method),
          friendRequestSenderID: request.owner.id,
        },
      });
      state.friendshipRequests[id].receivedAt = now;
    }
  }

  return events;
}

/**
 * Track accepted friend requests on the sender's side
 * (`senderAcknowledgedAt === 0`), returning the FriendAdd events to send and
 * deleting each request once its event is emitted. Mirrors
 * `trackReceivedFriendshipRequests`.
 */
export function trackAcceptedFriendshipRequests(
  state: MutableState,
  now: number,
): AnalyticsEvent[] {
  const events: AnalyticsEvent[] = [];
  const friendCount = getBuruFriendsCount(state);

  for (const [id, request] of Object.entries(state.friendshipRequests)) {
    if (
      request.status === 'accepted' &&
      request.owner.id === state.id &&
      request.senderAcknowledgedAt === 0
    ) {
      events.push({
        eventType: 'FriendAdd',
        eventProperties: {
          friendCount,
          method: getFriendshipRequestMethod(request.method),
          originFeature: request.originFeature,
          originSubFeature: request.originSubFeature,
          friendRequestReceiverID: request.target.id,
          friendRequestSenderID: request.owner.id,
          isReceiver: false,
        },
      });
      // Emitting the event was the request's last job on this side.
      delete state.friendshipRequests[id];
    }
  }

  return events;
}
